import React, { useEffect } from 'react';
import { cn, Badge, Icon, formatMessageStreaming } from '@ideoz/ui';
import { AlarmClock } from 'lucide-react';

export interface ChatThinkingProps {
  /**
   * Additional CSS classes
   */
  messages: any[];
  className?: string;
  content?: string;
}

const ChatThinking: React.FC<ChatThinkingProps> = ({ className, content, messages }) => {
  const [showNeedMoreTime, setShowNeedMoreTime] = React.useState(false);
  const [showAlmostReady, setShowAlmostReady] = React.useState(false);
  useEffect(() => {
    const needTimeId = setTimeout(() => {
      setShowNeedMoreTime(true);
    }, 15000);

    // Set patient banner after 30 seconds
    const timeoutId = setTimeout(() => {
      setShowAlmostReady(true);
      setShowNeedMoreTime(false);
    }, 30000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(needTimeId);
    };
  }, []);
  // Add the keyframes to the document head if not already present
  React.useEffect(() => {
    const styleId = 'bounce-high-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes bounce-high {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  const getIsRtl = () => {
    // Use detectedLanguage if available
    const message = messages[messages.length - 1];
    if (message.detectedLanguage) {
      return message.detectedLanguage === 'rtl';
    }
    // Fallback to content-based detection with detected language passed through
    return message.content
      ? formatMessageStreaming(message.content, {}, message.detectedLanguage).isRtl
      : false;
  };
  const isRtl = getIsRtl();
  return (
    <>
      {showNeedMoreTime && !showAlmostReady && (
        <Badge
          size="sm"
          variant="info"
          className="mb-6"
          leftIcon={<Icon icon={AlarmClock} size={18} className="stroke-current" />}
        >
          {isRtl
            ? 'يحتاج المزيد من الوقت للتفكير، وتحسين النتيجة من أجلك.'
            : 'It needs more time for thinking, optimizing the outcome for you.'}
        </Badge>
      )}

      {showAlmostReady && (
        <Badge
          size="sm"
          variant="warning"
          className="mb-6"
          leftIcon={<Icon icon={AlarmClock} size={18} className="stroke-current" />}
        >
          {isRtl
            ? 'نحن نجهز الأشياء لك - تقريباً انتهينا!'
            : "We're getting things ready for you — almost there!"}
        </Badge>
      )}
      <div className={cn('flex items-center gap-2', className)}>
        {/* AI Icon */}
        <svg
          id="LogoAiOutline"
          width={'20'}
          height={'20'}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="none"
        >
          <rect
            x="0.267133"
            y="0.267133"
            width="19.4657"
            height="19.4657"
            rx="9.73287"
            stroke="#98A2B3"
            stroke-width="0.534267"
          />
          <path
            d="M10.6158 13.8213C10.283 13.8213 9.99972 13.7789 9.76604 13.6939C9.53236 13.6019 9.34116 13.4533 9.19245 13.2481C9.04375 13.0358 8.93399 12.7492 8.86318 12.3882C8.79944 12.0273 8.76758 11.5779 8.76758 11.04C8.76758 10.5022 8.79944 10.0528 8.86318 9.69186C8.92691 9.33093 9.02959 9.04431 9.17121 8.83199C9.31992 8.61968 9.50757 8.46753 9.73417 8.37552C9.96785 8.28352 10.2511 8.23752 10.5839 8.23752C10.8176 8.23752 11.0336 8.25875 11.2319 8.30121C11.4372 8.3366 11.6178 8.40383 11.7736 8.50291C11.9364 8.59491 12.071 8.73292 12.1772 8.91692H12.2622V6.25781H13.6643V13.7364H12.3472L12.3047 13.0676H12.2303C12.1241 13.2799 11.9825 13.4392 11.8054 13.5453C11.6355 13.6515 11.4478 13.7223 11.2425 13.7576C11.0371 13.8001 10.8282 13.8213 10.6158 13.8213ZM11.2212 12.6112C11.4832 12.6112 11.6815 12.5793 11.8161 12.5156C11.9577 12.4448 12.0604 12.3351 12.1241 12.1865C12.1808 12.045 12.2162 11.8787 12.2303 11.6876C12.2516 11.4965 12.2622 11.2771 12.2622 11.0294C12.2622 10.7747 12.2516 10.5553 12.2303 10.3713C12.2162 10.1873 12.1808 10.0316 12.1241 9.90417C12.0604 9.7414 11.9577 9.62817 11.8161 9.56447C11.6815 9.4937 11.4832 9.45832 11.2212 9.45832C10.9876 9.45832 10.7999 9.47601 10.6583 9.51139C10.5167 9.5397 10.4104 9.60693 10.3396 9.71309C10.2759 9.81217 10.2334 9.96786 10.2122 10.1802C10.1909 10.3854 10.1803 10.6685 10.1803 11.0294C10.1803 11.3904 10.1909 11.677 10.2122 11.8893C10.2334 12.1016 10.2759 12.2608 10.3396 12.367C10.4104 12.4661 10.5167 12.5333 10.6583 12.5687C10.7999 12.597 10.9876 12.6112 11.2212 12.6112Z"
            fill="black"
          />
          <rect x="6.35352" y="6.25781" width="1.41353" height="1.41353" fill="black" />
          <rect x="6.35352" y="8.31787" width="1.41353" height="5.40912" fill="black" />
        </svg>
        {/* Thinking text */}
        <span className="text-gray-900 text-base font-normal">{content || 'Thinking'}</span>

        {/* Animated dots */}
        <div className="flex gap-1 ml-1">
          <div
            className="w-[4px] h-[4px] rounded-full"
            style={{
              backgroundColor: '#53389E',
              animation: 'bounce-high 0.8s infinite cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            }}
          />
          <div
            className="w-[4px] h-[4px] rounded-full"
            style={{
              backgroundColor: '#874FFF',
              animation: 'bounce-high 0.8s infinite cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              animationDelay: '0.1s',
            }}
          />
          <div
            className="w-[4px] h-[4px] rounded-full"
            style={{
              backgroundColor: '#7EE7E9',
              animation: 'bounce-high 0.8s infinite cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              animationDelay: '0.2s',
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ChatThinking;
